import React, { useState, useEffect } from 'react';
import 'react-quill/dist/quill.snow.css';
import { Box, Typography, Stack, FormHelperText, Tabs, Tab } from '@mui/material';
import { noticesApi } from '../../api/notices';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactQuillEditor from '../../components/Editor/ReactQuillEditor';
import PageLayout from '../../components/layout/PageLayout';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import ProgressBar from '../../components/common/ProgressBar';
import CancelConfirmDialog from '../../components/dialog/CancelConfirmDialog';
import BasicButton from '../../components/button/BasicButton';
import CustomTextField from '../../components/common/CustomTextField';
import RepresentativeImageSelector, {
  getRepresentativeImageUrl,
} from '../../components/common/RepresentativeImageSelector';
import theme from '../../theme';

const schema = yup.object().shape({
  title: yup.string().required('제목을 입력하세요.'),
  content: yup.string().required('설명을 입력하세요.'),
});

const NoticeForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');
  const returnPage = searchParams.get('returnPage');

  const {
    register,
    handleSubmit,
    control,
    setValue,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: '',
      titleEn: '',
      content: '',
      contentEn: '',
    },
  });

  const navigate = useNavigate();
  const content = watch('content');
  const [representativeImageUrl, setRepresentativeImageUrl] = useState('');
  const [initialImageUrl, setInitialImageUrl] = useState('');
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [editorLocale, setEditorLocale] = useState('ko');

  // 편집 모드일 때 기존 데이터 로드
  useEffect(() => {
    if (isEditMode) {
      const fetchNotice = async () => {
        try {
          const response = await noticesApi.getNoticeById(id);
          const notice = response.data;

          setValue('title', notice.title);
          setValue('titleEn', notice.titleEn || '');
          setValue('content', notice.content);
          setValue('contentEn', notice.contentEn || '');

          if (notice.imageUrl) {
            setRepresentativeImageUrl(notice.imageUrl);
            setInitialImageUrl(notice.imageUrl);
          }
        } catch (error) {
          console.error('Error fetching notice:', error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchNotice();
    }
  }, [id, isEditMode, setValue]);

  const onSubmit = async (formData) => {
    try {
      const imageUrl = getRepresentativeImageUrl(
        formData.content,
        representativeImageUrl,
        isEditMode ? initialImageUrl : '',
      );

      if (isEditMode) {
        await noticesApi.updateNotice(id, { ...formData, imageUrl });
        navigate(`/notice/${id}`);
      } else {
        // 생성 모드
        const response = await noticesApi.createNotice({ ...formData, imageUrl });
        navigate(`/notice/${response.data.slug || response.data.id}`);
      }
    } catch (error) {
      setError('root.server', {
        message: '저장하지 못했습니다. 입력 내용과 연결 상태를 확인해주세요.',
      });
    }
  };

  const handleCancel = () => {
    if (from === 'detail') {
      navigate(`/notice/${id}`);
    } else if (from === 'list' && returnPage) {
      navigate(`/notice?page=${returnPage}`);
    } else if (isEditMode) {
      navigate(`/notice/${id}`);
    } else {
      navigate('/notice');
    }
  };

  return (
    <PageLayout>
      <ProgressBar isLoading={isLoading}>
        <Box sx={{ maxWidth: '780px', mx: 'auto' }}>
          <Stack spacing={'30px'}>
            <Stack spacing={2.5}>
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  제목
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('title')}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  placeholder="제목을 입력하세요."
                />
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  영문 제목 (선택)
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('titleEn')}
                  placeholder="영문 제목이 없으면 한국어 제목을 표시합니다."
                />
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  본문
                </Typography>
                <Tabs
                  value={editorLocale}
                  onChange={(_, nextLocale) => setEditorLocale(nextLocale)}
                  aria-label="공지 본문 언어"
                >
                  <Tab value="ko" label="한국어" />
                  <Tab value="en" label="영어 (선택)" />
                </Tabs>
                <Controller
                  key={editorLocale}
                  name={editorLocale === 'ko' ? 'content' : 'contentEn'}
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <ReactQuillEditor value={field.value} onChange={field.onChange} />
                    </Box>
                  )}
                />
                <RepresentativeImageSelector
                  content={content}
                  value={representativeImageUrl}
                  onChange={setRepresentativeImageUrl}
                />
                {errors.content && (
                  <FormHelperText
                    error
                    sx={{
                      textAlign: 'left',
                      margin: '4px 0 0 0',
                      fontSize: '12px',
                      color: theme.palette.red,
                    }}
                  >
                    {errors.content.message}
                  </FormHelperText>
                )}
              </Stack>
            </Stack>

            {/* 버튼 */}
            {errors.root?.server && (
              <FormHelperText error>{errors.root.server.message}</FormHelperText>
            )}
            <Box width={'100%'} display={'flex'} justifyContent={'center'}>
              <Box display="flex" gap={1} width={{ xs: '100%', md: 'auto' }}>
                <BasicButton
                  fullWidth
                  variant="outlined"
                  onClick={() => setIsCancelDialogOpen(true)}
                  sx={{ width: { md: '200px', lg: '110px' }, height: '48px' }}
                >
                  취소
                </BasicButton>
                <BasicButton
                  fullWidth
                  type="submit"
                  disabled={isSubmitting}
                  onClick={handleSubmit(onSubmit)}
                  sx={{ width: { md: '200px', lg: '110px' }, height: '48px' }}
                >
                  등록
                </BasicButton>
              </Box>
            </Box>
          </Stack>
        </Box>
      </ProgressBar>
      <CancelConfirmDialog
        open={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        onConfirm={handleCancel}
      />
    </PageLayout>
  );
};

export default NoticeForm;
